import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import { Provider, useSelector } from 'react-redux';
import { store } from './store/store';

// ── Layout & Auth ────────────────────────────────────────────────────────────
import PettyCashLayout from './components/PettyCashLayout';
import Login from './pages/auth/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import MyDelegation from './pages/MyDelegation';
import './App.css';

// ── CEO Delegation ───────────────────────────────────────────────────────────
import { CEODelegationProvider, useCEODelegation } from './contexts/CEODelegationContext';
import { UserDelegationProvider } from './contexts/UserDelegationContext';
  import UserDelegationSettings from './pages/delegation/UserDelegationSettings';

// ── Shared / Utility ─────────────────────────────────────────────────────────
import ExternalQuoteForm from './components/ExternalQuoteForm';
import RequestDetails from './pages/PettyCash/RequestDetails';
import AccountSettings from './pages/PettyCash/AccountSettings';

// ── Legacy PettyCash ──────────────────────────────────────────────────────────
import PCDashboard from './pages/PettyCash/Dashboard';
import PCRequests from './pages/PettyCash/Requests';
import PCRequestForm from './pages/PettyCash/RequestForm';
import Position from './pages/PettyCash/Possition';
import Display from './pages/PettyCash/Display';

// ── Employee ──────────────────────────────────────────────────────────────────
import EmployeeCashRequests from './pages/employee/EmployeeCashRequests';
import EmployeeInvoices from './pages/employee/EmployeeInvoices';
import CashRequestForm from './pages/employee/CashRequestForm';
import JustificationForm from './pages/employee/JustificationForm';
import EditCashRequestForm from './pages/employee/EditCashRequestForm';
import ReimbursementRequestForm from './pages/employee/ReimbursementRequestForm';
import EmployeeIncidentReports from './pages/employee/EmployeeIncidentReports';
import EmployeeIncidentReportDetails from './pages/employee/EmployeeIncidentReportDetails';
import IncidentReportForm from './pages/employee/IncidentReportForm';
import EmployeeITSupport from './pages/employee/EmployeeITSupport';
import ITMaterialRequestForm from './pages/employee/ITMaterialRequestForm';
import ITIssueReportForm from './pages/employee/ITIssueReportForm';
import EmployeeITRequestDetails from './pages/employee/EmployeeITRequestDetails';
import EmployeeSuggestions from './pages/employee/EmployeeSuggestions';
import SuggestionForm from './pages/employee/SuggestionForm';
import SuggestionDetails from './pages/employee/SuggestionDetails';
import EmployeeSickLeave from './pages/employee/EmployeeLeaveManagement';
import SickLeaveForm from './pages/employee/LeaveRequestForm';
import EmployeeLeaveDetail from './pages/employee/EmployeeLeaveDetail';
import EmployeePurchaseRequisitions from './pages/employee/EmployeePurchaseRequisitions';
import PurchaseRequisitionForm from './pages/employee/PurchaseRequisitionForm';
import PurchaseRequisitionJustification from './pages/employee/PurchaseRequisitionJustification';
import PurchaseRequisitionDetails from './pages/employee/PurchaseRequisitionDetails';
import EnhancedActionItemsManagement from './pages/employee/EmployeeActionItemsManagement';
import TaskCompletionSubmission from './pages/employee/TaskCompletionSubmission';
import EmployeeKPIManagement from './pages/employee/EmployeeKPIManagement';
import EmployeePerformanceEvaluation from './pages/employee/EmployeePerformanceEvaluation';
import EmployeeProjectPlans from './pages/employee/EmployeeProjectPlan';
import EmployeeItemRequests from './pages/employee/EmployeeItemRequests';
import DepartmentBudgetDashboard from './pages/department/DepartmentBudgetDashboard';
import EngineeringIncidentReportForm from './pages/employee/EngineeringIncidentReportForm';
import EngineeringIncidentDashboard from './pages/supervisor/EngineeringIncidentDashboard';
import PublicEngineeringIncidentViewer from './pages/public/PublicEngineeringIncidentViewer';
import EmployeeEngineeringIncidentDetail from './pages/employee/EmployeeEngineeringIncidentDetail';

// ── Supplier (external portal) ────────────────────────────────────────────────
import SupplierPortal from './pages/supplier/SupplierPortal';
import SupplierInvoices from './pages/supplier/SupplierInvoices';
import SupplierProfile from './pages/supplier/SupplierProfile';
import SupplierLogin from './pages/supplier/SupplierLogin';
import SupplierRegistration from './pages/supplier/SupplierRegistration';
import ForgotPassword from './pages/supplier/ForgotPassword';
import ResetPassword from './pages/supplier/ResetPassword';

// ── Supervisor ────────────────────────────────────────────────────────────────
import SupervisorCashApprovals from './pages/supervisor/SupervisorCashApprovals';
import SupervisorInvoiceApprovals from './pages/supervisor/SupervisorInvoiceApprovals';
import SupervisorIncidentReports from './pages/supervisor/SupervisorIncidentReports';
import SupervisorSickLeaveApprovals from './pages/supervisor/SupervisorSickLeaveApprovals';
import SupervisorTeamDashboard from './pages/supervisor/SupervisorTeamDashboard';
import SupervisorPurchaseRequisitions from './pages/supervisor/SupervisorPurchaseRequisitions';
import SupervisorBudgetCodeApprovals from './pages/supervisor/SupervisorBudgetCodeApprovals';
import SupervisorITApprovals from './pages/supervisor/SupervisorITApprovals';
import SupervisorSuggestions from './pages/supervisor/SupervisorSuggestions';
import SupervisorJustificationForm from './pages/supervisor/SupervisorJustificationForm';
import SupervisorL2Review from './pages/supervisor/SupervisorL2Review';
import SupervisorL3FinalApproval from './pages/supervisor/SupervisorL3FinalApproval';
import SupervisorMilestoneDashboard from './pages/supervisor/SupervisorMilestoneDashboard';
import SupervisorMilestoneDetail from './pages/supervisor/SupervisorMilestoneDetail';
import SupervisorSubMilestoneDetail from './pages/supervisor/SupervisorSubMilestoneDetail';
import SupervisorActionItemsDashboard from './pages/supervisor/SupervisorActionItemsDashboard';
import SupervisorTaskCompletionApproval from './pages/supervisor/SupervisorTaskCompletionApproval';
import SupervisorActionItemCompletionApproval from './pages/supervisor/SupervisorActionItemCreationApproval';
import SupervisorTaskCreationApproval from './pages/supervisor/SupervisorTaskCreationApproval';
import SupervisorKPIApprovals from './pages/supervisor/SupervisorKPIApprovals';
import SupervisorBehavioralEvaluation from './pages/supervisor/SupervisorBehavioralEvaluation';
import SupervisorQuarterlyEvaluation from './pages/supervisor/SupervisorQuarterlyEvaluation';
import SupervisorPOApprovals from './pages/supervisor/SupervisorPOApprovals';
import SupervisorTenderApprovals from './pages/supervisor/SupervisorTenderApprovals';
import DebitNoteApprovals from './pages/supervisor/DebitNoteApprovals';
import ProjectPlanApproval from './pages/supervisor/ProjectPlanApproval';

// ── Finance ───────────────────────────────────────────────────────────────────
import FinanceApprovalList from './pages/finance/FinanceApprovalList';
import FinanceCashApprovals from './pages/finance/FinanceCashApprovals';
import FinanceInvoiceManagement from './pages/finance/FinanceInvoiceManagement';
import FinanceCashApprovalForm from './pages/finance/FinanceCashApprovalForm';
import FinanceJustificationForm from './pages/finance/FinanceJustificationForm';
import InvoiceAnalytics from './pages/finance/InvoiceAnalytics';
import FinanceInvoiceApproval from './pages/finance/FinanceInvoiceApproval';
import FinanceInvoicePreparation from './pages/finance/FinanceInvoicePreparation';
import FinanceDashboard from './pages/finance/FinanceDashboard';
import FinanceReports from './pages/finance/FinanceReports';
import FinanceSupplierManagement from './pages/finance/FinanceSupplierManagement';
import FinancePaymentProcessing from './pages/finance/FinancePaymentProcessing';
import FinancePurchaseRequisitions from './pages/finance/FinancePurchaseRequisitions';
import FinanceITApprovals from './pages/finance/FinanceITApprovals';
import FinanceBudgetCodeApprovals from './pages/finance/FinanceBudgetCodeApprovals';
import BudgetCodeDetails from './pages/finance/BudgetCodeDetails';
import ScheduledReportsManagement from './pages/finance/ScheduledReportsManagement';
import FinanceDisbursementPage from './pages/finance/FinanceDisbursementPage';
import FinanceAccountingCenter from './pages/finance/FinanceAccountingCenter';
import BudgetManagementDashboard from './pages/finance/BudgetManagementDashboard';
import BudgetAnalytics from './pages/finance/BudgetAnalytics';
import BudgetReports from './pages/finance/BudgetReports';
import BudgetRevisionsList from './pages/finance/BudgetRevisionsList';
import BudgetTransfersList from './pages/finance/BudgetTransfersList';
import SalaryPaymentForm from './pages/finance/SalaryPaymentForm';
import SalaryPaymentList from './pages/finance/SalaryPaymentList';
import SalaryPaymentDetails from './pages/finance/SalaryPaymentDetails';
import InvoiceManagementReports from './pages/finance/Invoicemanagementreports';

// ── Legal & Compliance ────────────────────────────────────────────────────────
import LegalComplianceCenter from './pages/legal/LegalComplianceCenter';

// ── Supply Chain ──────────────────────────────────────────────────────────────
import SupplyChainRequisitionManagement from './pages/supply-chain/SupplyChainRequisitionManagement';
import SupplyChainDashboard from './pages/supply-chain/SupplyChainDashboard';
import SupplyChainProcurementPlanning from './pages/supply-chain/SupplyChainProcurementPlanning';
import SupplyChainVendorManagement from './pages/supply-chain/SupplyChainVendorManagement';
import SupplyChainCustomerVendorManagement from './pages/supply-chain/SupplyChainCustomerVendorManagement';
import SupplyChainAnalytics from './pages/supply-chain/SupplyChainAnalytics';
import SupplyChainBuyerAssignment from './pages/supply-chain/SupplyChainBuyerAssignment';
import SupplyChainVendorOnboarding from './pages/supply-chain/SupplyChainSupplierOnboarding';
import SupplyChainContracts from './pages/supply-chain/SupplyChainContractManagement';
import SupplyChainProjectManagement from './pages/supply-chain/SupplyChainProjectManagement';
import SupplyChainInvoices from './pages/supply-chain/SupplyChainInvoiceManagement';
import SupplyChainItemManagement from './pages/supply-chain/SupplyChainItemManagement';
import SupplyChainPOManagement from './pages/supply-chain/SupplyChainPOManagement';
import InventoryDashboard from './pages/supply-chain/InventoryDashboard';
import InboundTransaction from './pages/supply-chain/InboundTransaction';
import OutboundTransaction from './pages/supply-chain/OutboundTransaction';
import InventoryItemDetails from './pages/supply-chain/InventoryItemDetails';
import FixedAssetRegistry from './pages/supply-chain/FixedAssetRegistry';
import AssetRegistrationForm from './pages/supply-chain/AssetRegistrationForm';
import AssetDetailsView from './pages/supply-chain/AssetDetailsView';
import SupplierPerformanceDashboard from './pages/supply-chain/SupplierPerformanceDashboard';
import SupplierPerformanceDetail from './pages/supply-chain/SupplierPerformanceDetail';
import DataMigration from './pages/supply-chain/DataMigration';
import DataMigrationValidator from './pages/supply-chain/DataMigrationValidator';
import ActionItemsManagement from './pages/supply-chain/ActionItemsManagement';
import UnifiedSupplierProfile from './pages/supply-chain/UnifiedSupplierProfile';
import SupplierBulkImport from './pages/supply-chain/SupplierBulkImport';
import ContractDetails from './pages/supply-chain/ContractDetails';
import ContractForm from './pages/supply-chain/ContractForm';
import InvoiceContractLinking from './pages/supply-chain/InvoiceContractLinking';
import InventoryReports from './pages/supply-chain/Inventoryreports';
import SupplierAnalyticsReport from './pages/supply-chain/SupplierAnalyticsReport';
import FixedAssetAnalytics from './pages/supply-chain/Fixedassetanalytics';
import SalaryPaymentReports from './pages/finance/SalaryPaymentReports'

// ── Buyer ─────────────────────────────────────────────────────────────────────
import BuyerRequisitionPortal from './pages/buyer/BuyerRequisitionPortal';
import BuyerDashboard from './pages/buyer/BuyerDashboard';
import BuyerProcurementTasks from './pages/buyer/BuyerProcurementTasks';
import BuyerQuoteManagement from './pages/buyer/BuyerQuoteManagement';
import BuyerSupplierManagement from './pages/buyer/BuyerSupplierManagement';
import BuyerPurchaseOrders from './pages/buyer/BuyerPurchaseOrders';
import BuyerTenderManagement from './pages/buyer/BuyerTenderManagement';
import BuyerDeliveryTracking from './pages/buyer/BuyerDeliveryTracking';
import BuyerPerformanceAnalytics from './pages/buyer/BuyerPerformanceAnalytics';
import PettyCashForms from './pages/buyer/PettyCashDashboard';
import DebitNoteManagement from './pages/buyer/DebitNoteManagement';

// ── HR ────────────────────────────────────────────────────────────────────────
import HRIncidentReports from './pages/hr/HRIncidentReports';
import HRSuggestions from './pages/hr/HRSuggestions';
import HRSickLeaveManagement from './pages/hr/HRLeaveManagement';
import HREmployeeWelfare from './pages/hr/HREmployeeWelfare';
import HRDashboard from './pages/hr/HRDashboard';
import HRPolicyManagement from './pages/hr/HRPolicyManagement';
import HREmployeeEngagement from './pages/hr/HREmployeeEngagement';
import EmployeeManagement from './pages/hr/EmployeeManagement';
import EmployeeForm from './pages/hr/EmployeeForm';
import EmployeeProfile from './pages/hr/EmployeeProfile';
import ContractManagement from './pages/hr/ContractManagement';
import DocumentsHub from './pages/hr/DocumentsHub';
import HRCommunicationsHub from './pages/hr/HRCommunicationsHub';
import HRReports from './pages/hr/Hrreports';

// ── IT ────────────────────────────────────────────────────────────────────────
import ITSupportRequests from './pages/it/ITSupportRequests';
import ITInventoryManagement from './pages/it/ITInventoryManagement';
import ITDashboard from './pages/it/ITDashboard';
import ITAssetManagement from './pages/it/ITAssetManagement';
import ITSystemMonitoring from './pages/it/ITSystemMonitoring';
import ITUserAccountManagement from './pages/it/ITUserAccountManagement';
import ITOrgStructure from './pages/it/ITOrgStructure';
import ITRequestDetails from './pages/it/ITRequestDetails';

// ── HSE ───────────────────────────────────────────────────────────────────────
import HSEIncidentReports from './pages/hse/HSEIncidentReports';
import HSEIncidentReportDetail from './pages/hse/HSEIncidentReportDetail';

// ── Admin ─────────────────────────────────────────────────────────────────────
import AdminRequestsList from './pages/admin/AdminRequestsList';
import AdminRequestDetails from './pages/admin/AdminRequestDetails';
import AdminIncidentReports from './pages/admin/AdminIncidentReports';
import AdminITSupport from './pages/admin/AdminITSupport';
import AdminSuggestions from './pages/admin/AdminSuggestions';
import AdminSickLeave from './pages/admin/AdminSickLeave';
import AdminAnalyticsDashboard from './pages/admin/AdminAnalyticsDashboard';
import AuditTrail from './pages/admin/AuditTrail';
import SalesCRM from './pages/sales/SalesCRM';
import Payroll from './pages/payroll/Payroll';
import Expenses from './pages/expenses/Expenses';
import Recruitment from './pages/recruitment/Recruitment';
import DocumentHub from './pages/documents/DocumentHub';
import DataExport from './pages/admin/DataExport';
import AdminSystemSettings from './pages/admin/AdminSystemSettings';
import AdminSupplierManagement from './pages/admin/AdminSupplierManagement';
import AdminUserManagement from './pages/admin/AdminUserManagement';
import AdminPurchaseRequisitions from './pages/admin/AdminPurchaseRequisitions';
import AdminBudgetCodeApprovals from './pages/admin/BudgetCodeApprovals';
import AdminCashApprovals from './pages/admin/AdminCashApprovals';
import SupplierApprovals from './pages/admin/SupplierApprovals';
import HeadApprovalPage from './pages/admin/HeadApprovalPage';
import PMMilestoneReviewDashboard from './pages/pm/PMMilestoneReviewDashboard';

// ── SharePoint ────────────────────────────────────────────────────────────────
import SharePointPortal from './pages/SharePoint/SharePointPortal';
import SharePointAdmin from './pages/SharePoint/SharePointAdmin';
import SharePointAnalytics from './pages/admin/SharePointAnalytics';
import MyUploads from './pages/SharePoint/MyUploads';
import AdminDashboard from './pages/SharePoint/AdminDashboard';
import Analytics from './pages/SharePoint/Analytics';

// ── Communications ────────────────────────────────────────────────────────────
import CommunicationsHub from './pages/admin/CommunicationsHub';
import NewCommunication from './pages/admin/NewCommunication';
import CommunicationsHistory from './pages/admin/CommunicationsHistory';
import CommunicationAnalytics from './pages/admin/CommunicationAnalytics';
import CommunicationTemplates from './pages/admin/CommunicationTemplates';
import CommunicationDetail from './pages/admin/CommunicationDetail';
import MyCommunications from './pages/employee/MyCommunications';

// ── Communications ────────────────────────────────────────────────────────────
// import CommunicationDetail from './pages/CEO/CEOAvailabilitySettings';
import CEOAvailabilitySettings from './pages/CEO/CEOAvailabilitySettings';



import DocumentUpload from './pages/employee/DocumentUpload';
import DocumentFieldPlacement from './pages/employee/DocumentFieldPlacement';
import DocumentSigningDashboard from './pages/employee/DocumentSigningDashboard';
import PublicSigningPage from './pages/public/PublicSigningPage';
import DocumentDetail from './pages/employee/DocumentDetail';


// ─────────────────────────────────────────────────────────────────────────────
// EnhancedProtectedRoute (v2 — CEO-delegation aware)
// Wraps ProtectedRoute with optional role-based access control.
// allowedRoles: if provided, user must be in the list OR be 'admin'/'ceo'
//               (unless the list contains 'admin-restricted'), OR hold
//               an active CEO delegation that grants an equivalent role.
// ─────────────────────────────────────────────────────────────────────────────
// Emails granted a narrow, specific exception to an otherwise role-gated route,
// independent of their actual role. Keep this list small and document why each
// entry exists - it's a routing-level convenience only; the real access boundary
// is enforced server-side.
const DOCUMENT_ONLY_HR_EMAILS = ['carmel.dafny@gratoglobal.com'];

const EnhancedProtectedRoute = ({ children, allowedRoles, fallbackRole = null }) => {
  const { user } = useSelector((state) => state.auth);
  const { grantedRoles, isGlobalDelegate } = useCEODelegation();
  const userRole = user?.role;

  if (allowedRoles && allowedRoles.length > 0) {
    const isAdmin           = userRole === 'admin';
    const isCEO              = userRole === 'ceo';
    const isAdminRestricted = allowedRoles.includes('admin-restricted');

    // Rule 1 — CEO and Admin bypass everything
    if ((isAdmin || isCEO) && !isAdminRestricted) {
      return <ProtectedRoute>{children}</ProtectedRoute>;
    }

    // Rule 1b — narrow, named-user exception (see DOCUMENT_ONLY_HR_EMAILS above)
    if (
      allowedRoles.includes('hr') &&
      user?.email &&
      DOCUMENT_ONLY_HR_EMAILS.includes(user.email.toLowerCase())
    ) {
      return <ProtectedRoute>{children}</ProtectedRoute>;
    }

    // Rule 2 — Global CEO delegate gets admin + finance access
    if (isGlobalDelegate && !isAdminRestricted) {
      return <ProtectedRoute>{children}</ProtectedRoute>;
    }

    // Rule 3 — Per-type delegation grants specific role access
    const hasDelegatedAccess =
      grantedRoles.length > 0 &&
      allowedRoles.some((requiredRole) => grantedRoles.includes(requiredRole));

    if (hasDelegatedAccess && !isAdminRestricted) {
      return <ProtectedRoute>{children}</ProtectedRoute>;
    }

    // Rule 4 — Normal direct role check
    // 'sales' staff are ordinary employees for every employee-level route.
    const hasDirectAccess = allowedRoles.includes(userRole) || (userRole === 'sales' && allowedRoles.includes('employee'));
    if (!hasDirectAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <ProtectedRoute>{children}</ProtectedRoute>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-route arrays
// React Router v6 requires all children of <Routes>/<Route> to be literal
// <Route> elements. These helpers return plain arrays of <Route> elements
// which are spread directly into the parent <Route> via {inventoryRoutes()}.
// ─────────────────────────────────────────────────────────────────────────────

const inventoryRoutes = () => [
  <Route key="inv-1"  path="inventory"                     element={<InventoryDashboard />} />,
  <Route key="inv-2"  path="inventory/inbound"              element={<InboundTransaction />} />,
  <Route key="inv-3"  path="inventory/outbound"             element={<OutboundTransaction />} />,
  <Route key="inv-4"  path="inventory/item/:itemId"         element={<InventoryItemDetails />} />,
  <Route key="inv-5"  path="inventory/items/:itemId"        element={<InventoryItemDetails />} />,
  <Route key="inv-6"  path="inventory/items/:itemId/audit"  element={<InventoryItemDetails />} />,
  <Route key="inv-7"  path="inventory/reports"                     element={<InventoryReports />} />,
];

const supplierMgmtRoutes = () => [
  <Route key="sm-1"  path="supplier-management"              element={<SupplyChainVendorManagement />} />,
  <Route key="sm-2"  path="suppliers"                         element={<SupplyChainVendorManagement />} />,
  <Route key="sm-3"  path="customer-vendor-management"        element={<SupplyChainCustomerVendorManagement />} />,
  <Route key="sm-4"  path="suppliers/:supplierId/profile"     element={<UnifiedSupplierProfile />} />,
  <Route key="sm-5"  path="supplier-onboarding"               element={<SupplyChainVendorOnboarding />} />,
  <Route key="sm-6"  path="supplier-approvals"                element={<SupplierApprovals />} />,
  <Route key="sm-7"  path="supplier-approvals/pending"        element={<SupplierApprovals />} />,
  <Route key="sm-8"  path="supplier-performance"              element={<SupplierPerformanceDashboard />} />,
  <Route key="sm-9"  path="supplier-performance/:supplierId"  element={<SupplierPerformanceDetail />} />,
];

const contractRoutes = () => [
  <Route key="cr-1"  path="contracts"                           element={<SupplyChainContracts />} />,
  <Route key="cr-2"  path="contracts/create"                    element={<ContractForm />} />,
  <Route key="cr-3"  path="contracts/:contractId"               element={<ContractDetails />} />,
  <Route key="cr-4"  path="contracts/:contractId/link-invoice"  element={<InvoiceContractLinking />} />,
];

const taskApprovalRoutes = () => [
  <Route key="ta-1"  path="action-items"                                        element={<SupervisorActionItemsDashboard />} />,
  <Route key="ta-2"  path="task-creation-approval/:taskId"                      element={<SupervisorTaskCreationApproval />} />,
  <Route key="ta-3"  path="task-completion-approval/:taskId/:assigneeId"        element={<SupervisorTaskCompletionApproval />} />,
  <Route key="ta-4"  path="action-items/:taskId/assignee/:assigneeId/review-l2" element={<SupervisorL2Review />} />,
  <Route key="ta-5"  path="action-items/:taskId/assignee/:assigneeId/review-l3" element={<SupervisorL3FinalApproval />} />,
];

const projectRoutes = () => [
  <Route key="pr-1"  path="project-management"        element={<SupplyChainProjectManagement />} />,
  <Route key="pr-2"  path="projects"                   element={<SupplyChainProjectManagement />} />,
  <Route key="pr-3"  path="projects/new"               element={<SupplyChainProjectManagement />} />,
  <Route key="pr-4"  path="projects/active"            element={<SupplyChainProjectManagement />} />,
  <Route key="pr-5"  path="projects/analytics"         element={<SupplyChainProjectManagement />} />,
  <Route key="pr-6"  path="projects/:projectId"        element={<RequestDetails />} />,
  <Route key="pr-7"  path="projects/:projectId/edit"   element={<SupplyChainProjectManagement />} />,
];

const communicationsRoutes = (prefix) => [
  <Route key="com-1" path="communications"           element={prefix === 'hr' ? <HRCommunicationsHub /> : <CommunicationsHub />} />,
  <Route key="com-2" path="communications/new"       element={<NewCommunication />} />,
  <Route key="com-3" path="communications/history"   element={<CommunicationsHistory />} />,
  <Route key="com-4" path="communications/scheduled" element={<CommunicationsHistory />} />,
  <Route key="com-5" path="communications/templates" element={<CommunicationTemplates />} />,
  ...(prefix === 'admin' ? [
    <Route key="com-6" path="communications/analytics" element={<CommunicationAnalytics />} />,
    <Route key="com-8" path="communications/:id/send"  element={<CommunicationDetail />} />,
  ] : []),
  <Route key="com-7" path="communications/:id"       element={<CommunicationDetail />} />,
  <Route key="com-9" path="communications/:id/edit"  element={<NewCommunication />} />,
];

// ─────────────────────────────────────────────────────────────────────────────
// AppRoutes
// ─────────────────────────────────────────────────────────────────────────────
const AppRoutes = () => {
  return (
    <Router>
      <Routes>

        {/* ── PUBLIC ROUTES ───────────────────────────────────────────────── */}
        <Route path="/login"                               element={<Login />} />
        {/* <Route path="/supplier/login"                      element={<SupplierLogin />} /> */}
        <Route path="/supplier/login"                      element={<Navigate to="/login" replace />} />
        <Route path="/supplier/forgot-password"            element={<ForgotPassword />} />
        <Route path="/supplier/reset-password/:token"      element={<ResetPassword />} />
        <Route path="/supplier/register"                   element={<SupplierRegistration />} />
        <Route path="/"                                    element={<Navigate to="/dashboard" replace />} />

        {/* ── PUBLIC ENGINEERING INCIDENT VIEWER (no auth required) ────── */}
        <Route path="/engineering-incidents/public/:token" element={<PublicEngineeringIncidentViewer />} />

        {/* External quote (no auth required) */}
        <Route path="/external-quote" element={
          <div style={{
            padding: '40px', textAlign: 'center', minHeight: '100vh',
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center', alignItems: 'center',
            backgroundColor: '#f5f5f5'
          }}>
            <div style={{
              maxWidth: '600px', padding: '40px',
              backgroundColor: 'white', borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h1 style={{ color: '#1890ff', marginBottom: '16px' }}>External Quote Submission</h1>
              <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.6' }}>
                This page is for external suppliers who have received an RFQ invitation.
                Please use the invitation link provided in your email to submit your quote.
              </p>
              <div style={{
                marginTop: '24px', padding: '16px',
                backgroundColor: '#f6ffed', borderRadius: '6px',
                border: '1px solid #b7eb8f'
              }}>
                <p style={{ margin: 0, color: '#52c41a' }}>
                  📧 Check your email for the secure invitation link to get started.
                </p>
              </div>
            </div>
          </div>
        } />
        <Route path="/external-quote/:token"        element={<ExternalQuoteForm />} />
        <Route path="/external-quote/:token/status" element={<ExternalQuoteForm />} />
        <Route path="/external-quote/:token/edit"   element={<ExternalQuoteForm />} />
        <Route path="/sign/:documentId/:token" element={<PublicSigningPage />} />

        {/* ── MAIN DASHBOARD ──────────────────────────────────────────────── */}
        <Route path="/dashboard" element={<ProtectedRoute><PettyCashLayout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
        </Route>

        <Route path="/delegation" element={<ProtectedRoute><PettyCashLayout /></ProtectedRoute>}>
          <Route index element={<MyDelegation />} />
        </Route>

        {/* ── SUPPLIER PORTAL ─────────────────────────────────────────────── */}
        <Route path="/supplier" element={
          <EnhancedProtectedRoute allowedRoles={['supplier']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index                            element={<Navigate to="/supplier/dashboard" replace />} />
          <Route path="dashboard"                 element={<SupplierInvoices />} />
          <Route path="portal"                    element={<SupplierPortal />} />
          <Route path="invoices"                  element={<SupplierInvoices />} />
          <Route path="invoices/submit"           element={<SupplierPortal />} />
          <Route path="invoices/:invoiceId"       element={<RequestDetails />} />
          <Route path="profile"                   element={<SupplierProfile />} />
          <Route path="settings"                  element={<SupplierProfile />} />
        </Route>

        {/* ── EMPLOYEE ────────────────────────────────────────────────────── */}
        <Route path="/employee" element={<ProtectedRoute><PettyCashLayout /></ProtectedRoute>}>

          {/* Cash requests */}
          <Route path="cash-requests"                      element={<EmployeeCashRequests />} />
          <Route path="cash-request/new"                   element={<CashRequestForm />} />
          <Route path="cash-request/reimbursement/new"     element={<ReimbursementRequestForm />} />
          <Route path="cash-request/:requestId"            element={<RequestDetails />} />
          <Route path="cash-request/:requestId/justify"    element={<JustificationForm />} />
          <Route path="cash-request/:requestId/edit"       element={<EditCashRequestForm />} />

          {/* Purchase requisitions */}
          <Route path="purchase-requisitions"              element={<EmployeePurchaseRequisitions />} />
          <Route path="purchase-requisitions/new"          element={<PurchaseRequisitionForm />} />
          <Route path="purchase-requisitions/:requisitionId" element={<PurchaseRequisitionDetails />} />

          {/* Invoices */}
          <Route path="invoices"                           element={<EmployeeInvoices />} />

          {/* Incident reports */}
          <Route path="incident-reports"                   element={<EmployeeIncidentReports />} />
          <Route path="incident-reports/new"               element={<IncidentReportForm />} />
          <Route path="incident-reports/:id"               element={<EmployeeIncidentReportDetails />} />
          <Route path="incident-reports/:id/edit"          element={<IncidentReportForm editMode={true} />} />
          <Route path="site-incident"          element={<EngineeringIncidentReportForm />} />
          <Route path="site-incident-reports/:id"               element={<EmployeeEngineeringIncidentDetail />} />

          {/* IT support */}
          <Route path="it-support"                         element={<EmployeeITSupport />} />
          <Route path="it-support/materials/new"           element={<ITMaterialRequestForm />} />
          <Route path="it-support/issues/new"              element={<ITIssueReportForm />} />
          <Route path="it-support/report-issue"            element={<ITIssueReportForm />} />
          <Route path="it-support/:requestId"              element={<EmployeeITRequestDetails />} />

          {/* Suggestions */}
          <Route path="suggestions"                        element={<EmployeeSuggestions />} />
          <Route path="suggestions/new"                    element={<SuggestionForm />} />
          <Route path="suggestions/:suggestionId"          element={<SuggestionDetails />} />

          {/* Leave management */}
          <Route path="leave"                              element={<EmployeeSickLeave />} />
          <Route path="leave/new"                          element={<SickLeaveForm />} />
          <Route path="leave/:requestId"                   element={<EmployeeLeaveDetail />} />
          <Route path="sick-leave"                         element={<Navigate to="/employee/leave" replace />} />
          <Route path="sick-leave/new"                     element={<Navigate to="/employee/leave/new" replace />} />

          {/* Item requests */}
          <Route path="item-requests"                      element={<EmployeeItemRequests />} />
          <Route path="item-requests/new"                  element={<EmployeeItemRequests />} />

          {/* Tasks & KPIs */}
          <Route path="tasks"                              element={<EnhancedActionItemsManagement />} />
          <Route path="action-items"                       element={<EnhancedActionItemsManagement />} />
          <Route path="task/:taskId/submit-completion"     element={<TaskCompletionSubmission />} />
          <Route path="kpis"                               element={<EmployeeKPIManagement />} />
          <Route path="performance-evaluation"             element={<EmployeePerformanceEvaluation />} />

          {/* Project plans */}
          <Route path="project-plans"                      element={<EmployeeProjectPlans />} />

          {/* Department budget */}
          <Route path="department-budget"                  element={<DepartmentBudgetDashboard />} />

          <Route path="documents/sign" element={<DocumentSigningDashboard />} />
          <Route path="documents/sign/new" element={<DocumentUpload />} />
          <Route path="documents/sign/:documentId" element={<DocumentDetail />} />
          {/* <Route path="documents/sign/:documentId" element={<DocumentSigningDashboard />} /> */}
          <Route path="documents/sign/:documentId/fields" element={<DocumentFieldPlacement />} />
        </Route>

        {/* ── SUPERVISOR ──────────────────────────────────────────────────── */}
        <Route path="/supervisor" element={
          <EnhancedProtectedRoute allowedRoles={[
            'supervisor','finance','hr','it','supply_chain',
            'project','buyer','admin','technical','manager','hse', 'ceo'
          ]}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route path="dashboard"                                         element={<SupervisorTeamDashboard />} />

          {/* Cash */}
          <Route path="cash-approvals"                                    element={<SupervisorCashApprovals />} />
          <Route path="cash-request/:requestId"                           element={<RequestDetails />} />
          <Route path="cash-approvals/justification/:requestId/review"    element={<SupervisorJustificationForm />} />

          {/* Projects */}
          <Route path="projects"                                          element={<SupplyChainProjectManagement />} />
          <Route path="projects/team"                                     element={<SupplyChainProjectManagement />} />
          <Route path="projects/new"                                      element={<SupplyChainProjectManagement />} />
          <Route path="projects/reports"                                  element={<SupplyChainProjectManagement />} />
          <Route path="projects/:projectId"                               element={<RequestDetails />} />

          {/* Purchase requisitions */}
          <Route path="purchase-requisitions"                             element={<SupervisorPurchaseRequisitions />} />
          <Route path="purchase-requisitions/:requisitionId"              element={<RequestDetails />} />

          {/* Invoices */}
          <Route path="invoice-approvals"                                 element={<SupervisorInvoiceApprovals />} />

          {/* Incidents */}
          <Route path="incident-reports"                                  element={<SupervisorIncidentReports />} />
          <Route path="incident-reports/pending"                          element={<SupervisorIncidentReports />} />
          <Route path="safety-review"                                     element={<SupervisorIncidentReports />} />
          <Route path="engineering-incident-reports"                      element={<EngineeringIncidentDashboard />} />
          {/* <Route path="engineering-incidents/public/:id"                      element={<PublicEngineeringIncidentViewer />} /> */}

          {/* Leave */}
          <Route path="sick-leave"                                        element={<SupervisorSickLeaveApprovals />} />
          <Route path="sick-leave/pending"                                element={<SupervisorSickLeaveApprovals />} />
          <Route path="team-calendar"                                     element={<SupervisorSickLeaveApprovals />} />

          {/* IT support */}
          <Route path="it-support"                                        element={<SupervisorITApprovals />} />
          <Route path="it-support/:requestId"                             element={<RequestDetails />} />

          {/* Suggestions */}
          <Route path="team-suggestions"                                  element={<SupervisorSuggestions />} />

          {/* Budget codes */}
          <Route path="budget-codes"                                      element={<SupervisorBudgetCodeApprovals />} />
          <Route path="budget-codes/:budgetId"                            element={<RequestDetails />} />

          {/* Milestones */}
          <Route path="milestones"                                        element={<SupervisorMilestoneDashboard />} />
          <Route path="milestone/:projectId/:milestoneId"                 element={<SupervisorMilestoneDetail />} />
          <Route path="sub-milestone/:projectId/:milestoneId/:subMilestoneId" element={<SupervisorSubMilestoneDetail />} />

          {/* Task & action item approvals */}
          {taskApprovalRoutes()}

          {/* KPI & performance */}
          <Route path="kpi-approvals"                                     element={<SupervisorKPIApprovals />} />
          <Route path="behavioral-evaluations"                            element={<SupervisorBehavioralEvaluation />} />
          <Route path="quarterly-evaluations"                             element={<SupervisorQuarterlyEvaluation />} />

          {/* PO / tenders / debit notes */}
          <Route path="po-approvals"                                      element={<SupervisorPOApprovals />} />
          <Route path="po-approvals/:poId"                                element={<RequestDetails />} />
          <Route path="purchase-order-approvals"                          element={<SupervisorPOApprovals />} />
          <Route path="tender-approvals"                                  element={<SupervisorTenderApprovals />} />
          <Route path="debit-note-approvals"                              element={<DebitNoteApprovals />} />

          {/* Project plan approvals */}
          <Route path="project-plan-approvals"                            element={<ProjectPlanApproval />} />

          {/* Department budget */}
          <Route path="department-budget"                                 element={<DepartmentBudgetDashboard />} />
        </Route>

        {/* ── BUYER ───────────────────────────────────────────────────────── */}
        <Route path="/buyer" element={
          <EnhancedProtectedRoute allowedRoles={['buyer','supply_chain','admin', 'ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route path="dashboard"                                element={<BuyerDashboard />} />

          {/* Requisitions */}
          <Route path="requisitions"                             element={<BuyerRequisitionPortal />} />
          <Route path="requisitions/:requisitionId"              element={<PurchaseRequisitionDetails />} />
          <Route path="requisitions/:requisitionId/justify"      element={<PurchaseRequisitionJustification />} />

          {/* Procurement */}
          <Route path="procurement"                              element={<BuyerProcurementTasks />} />
          <Route path="procurement/:taskId"                      element={<PurchaseRequisitionDetails />} />

          {/* Quotes */}
          <Route path="quotes"                                   element={<BuyerQuoteManagement />} />
          <Route path="quotes/:quoteId"                          element={<PurchaseRequisitionDetails />} />
          <Route path="quotes/:quoteId/evaluate"                 element={<BuyerQuoteManagement />} />
          <Route path="quotes/:quoteId/compare"                  element={<BuyerQuoteManagement />} />

          {/* Suppliers */}
          <Route path="suppliers"                                element={<BuyerSupplierManagement />} />
          <Route path="suppliers/:supplierId"                    element={<RequestDetails />} />
          <Route path="suppliers/:supplierId/performance"        element={<BuyerSupplierManagement />} />
          <Route path="suppliers/:supplierId/communication"      element={<BuyerSupplierManagement />} />
          <Route path="supplier-performance"                     element={<SupplierPerformanceDashboard />} />

          {/* Purchase orders */}
          <Route path="purchase-orders"                          element={<BuyerPurchaseOrders />} />
          <Route path="purchase-orders/new"                      element={<BuyerPurchaseOrders />} />
          <Route path="purchase-orders/:poId"                    element={<RequestDetails />} />
          <Route path="purchase-orders/:poId/edit"               element={<BuyerPurchaseOrders />} />

          {/* Tenders */}
          <Route path="tenders"                                  element={<BuyerTenderManagement />} />
          <Route path="tenders/:tenderId"                        element={<RequestDetails />} />
          <Route path="tenders/:tenderId/evaluate"               element={<BuyerTenderManagement />} />
          <Route path="tenders/:tenderId/compare"                element={<BuyerTenderManagement />} />

          {/* Deliveries */}
          <Route path="deliveries"                               element={<BuyerDeliveryTracking />} />
          <Route path="deliveries/:deliveryId"                   element={<RequestDetails />} />
          <Route path="deliveries/:deliveryId/track"             element={<BuyerDeliveryTracking />} />

          {/* Analytics */}
          <Route path="analytics"                                element={<BuyerPerformanceAnalytics />} />
          <Route path="analytics/procurement"                    element={<BuyerPerformanceAnalytics />} />
          <Route path="analytics/suppliers"                      element={<BuyerPerformanceAnalytics />} />
          <Route path="analytics/performance"                    element={<BuyerPerformanceAnalytics />} />
          <Route path="reports"                                  element={<BuyerPerformanceAnalytics />} />
          <Route path="reports/procurement"                      element={<BuyerPerformanceAnalytics />} />
          <Route path="reports/savings"                          element={<BuyerPerformanceAnalytics />} />

          {/* Petty cash / debit notes */}
          <Route path="petty-cash"                               element={<PettyCashForms />} />
          <Route path="debit-notes"                              element={<DebitNoteManagement />} />

          {/* Action items */}
          <Route path="action-items"                             element={<ActionItemsManagement />} />
          <Route path="action-items/:projectId"                  element={<ActionItemsManagement />} />

          {/* Inventory (buyer access) */}
          {inventoryRoutes()}
        </Route>

        {/* ── SUPPLY CHAIN ────────────────────────────────────────────────── */}
        <Route path="/supply-chain" element={
          <EnhancedProtectedRoute allowedRoles={['supply_chain','project','buyer','admin','finance', 'ceo', 'it']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route path="dashboard"                        element={<SupplyChainDashboard />} />

          {/* Requisitions */}
          <Route path="requisitions"                     element={<SupplyChainRequisitionManagement />} />
          <Route path="requisitions/:requisitionId"      element={<RequestDetails />} />

          {/* Projects */}
          {projectRoutes()}

          {/* Procurement */}
          <Route path="procurement-planning"             element={<SupplyChainProcurementPlanning />} />
          <Route path="analytics"                        element={<SupplyChainAnalytics />} />
          <Route path="buyer-assignment"                 element={<SupplyChainBuyerAssignment />} />
          <Route path="item-management"                  element={<SupplyChainItemManagement />} />

          {/* Action items */}
          <Route path="action-items"                     element={<ActionItemsManagement />} />
          <Route path="action-items/:projectId"          element={<ActionItemsManagement />} />

          {/* Inventory */}
          {inventoryRoutes()}

          {/* Fixed assets */}
          <Route path="fixed-assets"                     element={<FixedAssetRegistry />} />
          <Route path="fixed-assets/register"            element={<AssetRegistrationForm />} />
          <Route path="fixed-assets/:assetTag"           element={<AssetDetailsView />} />
          <Route path="fixed-assets/analytics"           element={<FixedAssetAnalytics />} />


          {/* Data migration */}
          <Route path="data-migration"                   element={<DataMigration />} />
          <Route path="data-migration-validator"         element={<DataMigrationValidator />} />

          {/* Supplier management */}
          {supplierMgmtRoutes()}
          <Route path="supplier-approvals"                       element={<SupplierApprovals />} />
          <Route path="supplier-approvals/statistics"    element={<SupplierApprovals />} />
          <Route path="vendor-onboarding"                element={<SupplyChainVendorOnboarding />} />
          <Route path="suppliers/bulk-import"            element={<SupplierBulkImport />} />
          <Route path="suppliers/reports"            element={<SupplierAnalyticsReport />} />

          {/* Contracts */}
          {contractRoutes()}

          {/* Invoices */}
          <Route path="supplier-invoices"                element={<SupplyChainInvoices />} />

          {/* Head approval */}
          <Route path="head-approval"                    element={<HeadApprovalPage />} />

          {/* Purchase orders */}
          <Route path="purchase-orders"                  element={<SupplyChainPOManagement />} />
          <Route path="purchase-orders/pending"          element={<SupplyChainPOManagement />} />
          <Route path="purchase-orders/:poId"            element={<RequestDetails />} />
        </Route>

        {/* ── PROJECT ROLE ─────────────────────────────────────────────────── */}
        <Route path="/project" element={
          <EnhancedProtectedRoute allowedRoles={['project','admin', 'ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          {/* Projects */}
          {projectRoutes()}

          {/* Action items */}
          <Route path="action-items"                                         element={<ActionItemsManagement />} />
          <Route path="action-items/:projectId"                              element={<ActionItemsManagement />} />

          {/* Milestones */}
          <Route path="milestones"                                           element={<SupervisorMilestoneDashboard />} />
          <Route path="milestone/:projectId/:milestoneId"                    element={<SupervisorMilestoneDetail />} />

          {/* Task approvals */}
          <Route path="task-approvals"                                       element={<SupervisorActionItemsDashboard />} />
          <Route path="task-completion-approval/:taskId/:assigneeId"         element={<SupervisorTaskCompletionApproval />} />
        </Route>

        {/* ── FINANCE ─────────────────────────────────────────────────────── */}
        <Route path="/finance" element={
          <EnhancedProtectedRoute allowedRoles={['finance','admin', 'ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route path="dashboard"                                element={<FinanceDashboard />} />

          {/* Cash */}
          <Route path="cash-approvals"                           element={<FinanceCashApprovals />} />
          <Route path="cash-request/:requestId"                  element={<FinanceCashApprovalForm />} />
          <Route path="justification/:requestId"                 element={<FinanceJustificationForm />} />
          <Route path="cash-management"                          element={<FinanceCashApprovals />} />
          <Route path="cash-reports"                             element={<FinanceReports />} />

          {/* Purchase requisitions */}
          <Route path="purchase-requisitions"                    element={<FinancePurchaseRequisitions />} />
          <Route path="purchase-requisitions/:requisitionId"     element={<RequestDetails />} />

          {/* IT */}
          <Route path="it-approvals"                             element={<FinanceITApprovals />} />
          <Route path="it-approvals/:requestId"                  element={<RequestDetails />} />

          {/* Invoices */}
          <Route path="invoice-management"                       element={<FinanceInvoiceApproval />} />
          <Route path="prepare-invoice"                          element={<FinanceInvoicePreparation />} />
          <Route path="invoice-analytics"                        element={<InvoiceAnalytics />} />
          <Route path="invoice-management/reports"                        element={<InvoiceManagementReports />} />

          {/* Payments */}
          <Route path="payments"                                 element={<FinancePaymentProcessing />} />
          <Route path="disbursements"                            element={<FinanceDisbursementPage />} />

          {/* Reports & analytics */}
          <Route path="reports"                                  element={<FinanceReports />} />
          <Route path="analytics"                                element={<InvoiceAnalytics />} />

          {/* Budget codes */}
          <Route path="budget-codes"                             element={<FinanceBudgetCodeApprovals />} />
          <Route path="budget-codes/pending"                     element={<FinanceBudgetCodeApprovals />} />
          <Route path="budget-codes/:budgetId"                   element={<RequestDetails />} />
          <Route path="budget-codes/:budgetId/approve"           element={<FinanceBudgetCodeApprovals />} />
          <Route path="budget-codes/:codeId/detail"              element={<BudgetCodeDetails />} />

          {/* Budget management */}
          <Route path="budget-management"                        element={<BudgetManagementDashboard />} />
          <Route path="budget-reports"                           element={<BudgetReports />} />
          <Route path="budget-analytics"                         element={<BudgetAnalytics />} />
          <Route path="budget-revisions"                         element={<BudgetRevisionsList />} />
          <Route path="budget-transfers"                         element={<BudgetTransfersList />} />

          {/* Scheduled reports */}
          <Route path="scheduled-reports"                        element={<ScheduledReportsManagement />} />

          {/* Accounting */}
          <Route path="accounting"                               element={<FinanceAccountingCenter />} />

          {/* Supplier management (finance view) */}
          <Route path="suppliers"                                element={<FinanceSupplierManagement />} />
          <Route path="supplier-management"                      element={<SupplyChainVendorManagement />} />
          <Route path="customer-vendor-management"               element={<SupplyChainCustomerVendorManagement />} />
          <Route path="suppliers/:supplierId/profile"            element={<UnifiedSupplierProfile />} />
          <Route path="supplier-onboarding"                      element={<SupplyChainVendorOnboarding />} />
          <Route path="supplier-performance"                     element={<SupplierPerformanceDashboard />} />
          <Route path="supplier-performance/:supplierId"         element={<SupplierPerformanceDetail />} />
          <Route path="suppliers/bulk-import"                    element={<SupplierBulkImport />} />
          <Route path="supplier-approvals"                       element={<SupplierApprovals />} />
          <Route path="supplier-approvals/pending"               element={<SupplierApprovals />} />

          {/* Contracts */}
          {contractRoutes()}

          {/* Salary */}
          <Route path="salary-payments"                          element={<SalaryPaymentList />} />
          <Route path="salary-payments/new"                      element={<SalaryPaymentForm />} />
          <Route path="salary-payments/:id"                      element={<SalaryPaymentDetails />} />
          <Route path="salary-payments/reports"                      element={<SalaryPaymentReports />} />
        </Route>

        {/* ── LEGAL & COMPLIANCE ──────────────────────────────────────────── */}
        <Route path="/legal" element={
          <EnhancedProtectedRoute allowedRoles={['legal','finance','admin', 'ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index                       element={<LegalComplianceCenter />} />
          <Route path="dashboard"            element={<LegalComplianceCenter />} />
          <Route path="sdd"                  element={<LegalComplianceCenter />} />
          <Route path="sdd/:id"              element={<LegalComplianceCenter />} />
          <Route path="contracts"            element={<LegalComplianceCenter />} />
          <Route path="contracts/:id"        element={<LegalComplianceCenter />} />
          <Route path="regulatory"           element={<LegalComplianceCenter />} />
          <Route path="risks"                element={<LegalComplianceCenter />} />
          <Route path="analytics"            element={<LegalComplianceCenter />} />
        </Route>

        {/* ── HR ──────────────────────────────────────────────────────────── */}
        <Route path="/hr" element={
          <EnhancedProtectedRoute allowedRoles={['hr','admin', 'ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route path="dashboard"                          element={<HRDashboard />} />

          {/* Employees */}
          <Route path="employees"                          element={<EmployeeManagement />} />
          <Route path="employees/new"                      element={<EmployeeForm />} />
          <Route path="employees/:id"                      element={<EmployeeProfile />} />
          <Route path="employees/:id/edit"                 element={<EmployeeForm />} />

          {/* Contracts & documents */}
          <Route path="contracts"                          element={<ContractManagement />} />
          <Route path="documents"                          element={<DocumentsHub />} />

          {/* Incidents */}
          <Route path="incident-reports"                   element={<HRIncidentReports />} />
          <Route path="incident-reports/analytics"         element={<HRIncidentReports />} />
          <Route path="incident-reports/:reportId"         element={<RequestDetails />} />
          <Route path="incident-investigation"             element={<HRIncidentReports />} />

          {/* Policies */}
          <Route path="safety-policies"                    element={<HRPolicyManagement />} />
          <Route path="leave-policies"                     element={<HRPolicyManagement />} />
          <Route path="policy-management"                  element={<HRPolicyManagement />} />

          {/* Suggestions */}
          <Route path="suggestions"                        element={<HRSuggestions />} />
          <Route path="suggestions/analytics"              element={<HRSuggestions />} />
          <Route path="suggestions/:suggestionId"          element={<RequestDetails />} />
          <Route path="suggestion-implementation"          element={<HRSuggestions />} />

          {/* Leave management */}
          <Route path="sick-leave"                         element={<HRSickLeaveManagement />} />
          <Route path="sick-leave/analytics"               element={<HRSickLeaveManagement />} />
          <Route path="sick-leave/:leaveId"                element={<RequestDetails />} />
          <Route path="medical-certificates"               element={<HRSickLeaveManagement />} />

          {/* Engagement */}
          <Route path="employee-engagement"                element={<HREmployeeEngagement />} />

          {/* Communications */}
          {communicationsRoutes('hr')}

          {/* Reports */}
          <Route path="reports"                            element={<HRReports />} />
        </Route>

        {/* ── IT ──────────────────────────────────────────────────────────── */}
        <Route path="/it" element={
          <EnhancedProtectedRoute allowedRoles={['it','admin', 'ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route path="dashboard"                          element={<ITDashboard />} />
          <Route path="support-requests"                   element={<ITSupportRequests />} />
          <Route path="support-requests/:requestId"        element={<ITRequestDetails />} />
          <Route path="approvals"                          element={<ITDashboard />} />
          <Route path="asset-management"                   element={<ITAssetManagement />} />
          <Route path="inventory"                          element={<ITInventoryManagement />} />
          <Route path="system-monitoring"                  element={<ITSystemMonitoring />} />
          <Route path="user-accounts"                      element={<ITUserAccountManagement />} />
          <Route path="org-structure"                      element={<ITOrgStructure />} />
          <Route path="it-support/:requestId"              element={<ITRequestDetails />} />

          {/* Communications */}
          {communicationsRoutes('it')}
        </Route>

        {/* ── HSE ─────────────────────────────────────────────────────────── */}
        <Route path="/hse" element={
          <EnhancedProtectedRoute allowedRoles={['hse','admin', 'ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route path="dashboard"                          element={<HSEIncidentReports />} />
          <Route path="incident-reports"                   element={<HSEIncidentReports />} />
          <Route path="incident-reports/:id"               element={<HSEIncidentReportDetail />} />
          <Route path="incident-analytics"                 element={<HSEIncidentReports />} />
        </Route>

        {/* ── ADMIN ───────────────────────────────────────────────────────── */}
        <Route path="/admin" element={
          <EnhancedProtectedRoute allowedRoles={['admin', 'ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route path="dashboard"                          element={<AdminAnalyticsDashboard />} />

          {/* Cash */}
          <Route path="cash-approvals"                     element={<AdminCashApprovals />} />
          <Route path="cash-management"                    element={<SupervisorCashApprovals />} />
          <Route path="cash-analytics"                     element={<AdminAnalyticsDashboard />} />
          <Route path="cash-users"                         element={<AdminUserManagement />} />

          {/* Projects */}
          {projectRoutes()}
          <Route path="projects/resources"                 element={<SupplyChainProjectManagement />} />

          {/* Purchase requisitions */}
          <Route path="purchase-requisitions"              element={<AdminPurchaseRequisitions />} />
          <Route path="purchase-analytics"                 element={<AdminAnalyticsDashboard />} />
          <Route path="procurement-budget"                 element={<AdminAnalyticsDashboard />} />

          {/* Invoices */}
          <Route path="invoice-management"                 element={<AdminRequestsList />} />
          <Route path="invoice-approvals"                  element={<SupervisorInvoiceApprovals />} />
          <Route path="invoice-settings"                   element={<AdminSystemSettings />} />

          {/* Suppliers */}
          <Route path="suppliers"                          element={<AdminSupplierManagement />} />
          <Route path="suppliers/dashboard"                element={<AdminSupplierManagement />} />
          <Route path="suppliers/:supplierId"              element={<RequestDetails />} />
          <Route path="suppliers/:supplierId/profile"      element={<UnifiedSupplierProfile />} />
          <Route path="suppliers/bulk-import"              element={<SupplierBulkImport />} />
          <Route path="supplier-approvals"                 element={<SupplierApprovals />} />
          <Route path="supplier-approvals/pending"         element={<SupplierApprovals />} />
          <Route path="supplier-approvals/dashboard"       element={<SupplierApprovals />} />
          <Route path="supplier-approvals/:supplierId"     element={<RequestDetails />} />

          {/* Incidents */}
          <Route path="incident-reports"                   element={<EngineeringIncidentDashboard />} />
          <Route path="incident-reports/dashboard"         element={<EngineeringIncidentDashboard />} />
          <Route path="incident-reports/:reportId"         element={<RequestDetails />} />
          <Route path="incident-compliance"                element={<EngineeringIncidentDashboard />} />

          {/* IT support */}
          <Route path="it-support"                         element={<AdminITSupport />} />
          <Route path="it-support/dashboard"               element={<AdminITSupport />} />
          <Route path="it-support/:requestId"              element={<RequestDetails />} />
          <Route path="it-budget"                          element={<AdminAnalyticsDashboard />} />
          <Route path="buyer-management"                   element={<AdminUserManagement />} />
          <Route path="workflow-config"                    element={<AdminSystemSettings />} />

          {/* Suggestions */}
          <Route path="suggestions"                        element={<AdminSuggestions />} />
          <Route path="suggestions/reports"                element={<AdminSuggestions />} />
          <Route path="suggestions/:suggestionId"          element={<RequestDetails />} />
          <Route path="strategic-suggestions"              element={<AdminSuggestions />} />

          {/* Leave */}
          <Route path="sick-leave"                         element={<AdminSickLeave />} />
          <Route path="sick-leave/reports"                 element={<AdminSickLeave />} />
          <Route path="sick-leave/:leaveId"                element={<RequestDetails />} />
          <Route path="leave-compliance"                   element={<AdminSickLeave />} />

          {/* Analytics & system */}
          <Route path="analytics"                          element={<AdminAnalyticsDashboard />} />
          <Route path="system-settings"                    element={<AdminSystemSettings />} />
          <Route path="user-management"                    element={<AdminUserManagement />} />

          {/* Budget codes */}
          <Route path="budget-codes"                       element={<AdminBudgetCodeApprovals />} />
          <Route path="budget-codes/pending"               element={<AdminBudgetCodeApprovals />} />
          <Route path="budget-codes/analytics"             element={<AdminAnalyticsDashboard />} />
          <Route path="budget-codes/:budgetId"             element={<RequestDetails />} />
          <Route path="budget-code-config"                 element={<AdminSystemSettings />} />

          {/* Action items */}
          <Route path="action-items"                       element={<ActionItemsManagement />} />
          <Route path="action-items/:projectId"            element={<ActionItemsManagement />} />

          {/* Communications */}
          {communicationsRoutes('admin')}

          {/* Approvals */}
          <Route path="head-approval"                      element={<HeadApprovalPage />} />
          <Route path="pm/milestone-review"                element={<PMMilestoneReviewDashboard />} />

          {/* Inventory */}
          {inventoryRoutes()}

          {/* Legal (admin passthrough) */}
          <Route path="legal/*"                            element={<LegalComplianceCenter />} />

          {/* Salary */}
          <Route path="salary-payments"                    element={<SalaryPaymentList />} />
          <Route path="salary-analytics"                   element={<AdminAnalyticsDashboard />} />
        </Route>

        {/* ── CEO ─────────────────────────────────────────────────────────── */}
        <Route path="/ceo" element={
          <EnhancedProtectedRoute allowedRoles={['ceo', 'admin']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          {/* Dashboard — standard module-card view with CEO role config */}
          <Route index                                   element={<Navigate to="/ceo/dashboard" replace />} />
          <Route path="dashboard"                        element={<Dashboard />} />
 
          {/* Strategic Overview */}
          <Route path="overview"                         element={<AdminAnalyticsDashboard />} />
          <Route path="reports"                          element={<AdminAnalyticsDashboard />} />
          <Route path="analytics"                        element={<AdminAnalyticsDashboard />} />
          <Route path="company-reports"                  element={<FinanceReports />} />
 
          {/* Financial */}
          <Route path="cash-overview"                    element={<FinanceCashApprovals />} />
          <Route path="financial-overview"               element={<FinanceDashboard />} />
          <Route path="financial-reports"                element={<FinanceReports />} />
          <Route path="budget-analytics"                 element={<BudgetAnalytics />} />
          <Route path="payment-analytics"                element={<InvoiceAnalytics />} />
          <Route path="invoice-overview"                 element={<FinanceInvoiceManagement />} />
 
          {/* Procurement & Supply Chain */}
          <Route path="procurement-overview"             element={<SupplyChainDashboard />} />
          <Route path="procurement-reports"              element={<SupplyChainAnalytics />} />
          <Route path="supplier-overview"                element={<AdminSupplierManagement />} />
          <Route path="supplier-performance"             element={<SupplierPerformanceDashboard />} />
          <Route path="contract-analytics"               element={<SupplyChainContracts />} />
 
          {/* Projects & Performance */}
          <Route path="project-overview"                 element={<SupplyChainProjectManagement />} />
          <Route path="projects"                         element={<SupplyChainProjectManagement />} />
          <Route path="projects/:projectId"              element={<RequestDetails />} />
          <Route path="strategic-reports"                element={<AdminAnalyticsDashboard />} />
          <Route path="company-kpis"                     element={<SupervisorKPIApprovals />} />
          <Route path="performance"                      element={<AdminAnalyticsDashboard />} />
          <Route path="performance-reports"              element={<AdminAnalyticsDashboard />} />
 
          {/* Operations */}
          <Route path="tasks-overview"                   element={<ActionItemsManagement />} />
          <Route path="employee-overview"                element={<EmployeeManagement />} />
          <Route path="hr-analytics"                     element={<HRDashboard />} />
 
          {/* System & Audit */}
          <Route path="system-overview"                  element={<AdminAnalyticsDashboard />} />
          <Route path="audit-logs"                       element={<AdminSystemSettings />} />
          <Route path="availability-settings"           element={<CEOAvailabilitySettings />} />
        </Route>

        {/* ── CEO DELEGATE MIRROR ROUTES ──────────────────────────────────
            Role-neutral routes for pages that are normally behind finance/admin
            guards but need to be reachable by a CEO delegate of any role.
            Access here only matters after the chain has been mutated to put
            the delegate's email in the CEO approval step — the page components
            filter by approver email, so a non-delegate who lands here sees
            nothing actionable.
        ─────────────────────────────────────────────────────────────────── */}
        <Route
          path="/ceo-delegate"
          element={
            <ProtectedRoute>
              <PettyCashLayout />
            </ProtectedRoute>
          }
        >
          {/* Budget codes — mirrors /finance/budget-codes */}
          <Route
            path="budget-codes"
            element={<FinanceBudgetCodeApprovals />}
          />

          {/* Supplier approvals — mirrors /admin/supplier-approvals */}
          <Route
            path="supplier-approvals"
            element={<SupplierApprovals />}
          />

          {/* Salary payments — mirrors /finance/salary-payments */}
          <Route
            path="salary-payments"
            element={<SalaryPaymentList />}
          />

          {/* Invoice approvals — mirrors /finance/invoice-management */}
          <Route
            path="invoice-approvals"
            element={<FinanceInvoiceApproval />}
          />

          {/* Purchase requisition head approval */}
          <Route
            path="purchase-requisitions"
            element={<HeadApprovalPage />}
          />
        </Route>

        {/* ── LEGACY PETTYCASH ────────────────────────────────────────────── */}
        <Route path="/pettycash" element={
          <EnhancedProtectedRoute allowedRoles={['finance', 'admin', 'admin']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index                                     element={<AdminAnalyticsDashboard />} />
          <Route path="dashboard"                          element={<AdminAnalyticsDashboard />} />
          <Route path="requests"                           element={<PCRequests />} />
          <Route path="requests/new"                       element={<PCRequestForm />} />
          <Route path="requests/:id"                       element={<PCRequestForm editMode />} />
          <Route path="request-details/:id"                element={<RequestDetails />} />
          <Route path="cash-requests"                      element={<AdminRequestsList />} />
          <Route path="cash-requests/:requestId"           element={<AdminRequestDetails />} />
          <Route path="settings"                           element={<AccountSettings />} />
          <Route path="account-settings"                   element={<AccountSettings />} />
          <Route path="position"                           element={<Position />} />
          <Route path="display"                            element={<Display />} />
          <Route path="request-form"                       element={<PCRequestForm />} />
        </Route>

        {/* ── SHAREPOINT ──────────────────────────────────────────────────── */}
        <Route path="/sharepoint" element={<ProtectedRoute><PettyCashLayout /></ProtectedRoute>}>
          <Route path="portal"                             element={<SharePointPortal />} />
          <Route path="my-files"                           element={<MyUploads />} />
          <Route path="admin"                              element={<AdminDashboard />} />
          <Route path="analytics"                          element={<Analytics />} />
          <Route path="manage"                             element={<AdminDashboard />} />
          <Route path="access"                             element={<AdminDashboard />} />
        </Route>

        {/* ── UNIVERSAL / MISC ────────────────────────────────────────────── */}
        <Route path="/account-settings" element={<ProtectedRoute><PettyCashLayout /></ProtectedRoute>}>
          <Route index element={<AccountSettings />} />
        </Route>

        <Route path="/analytics" element={
          <EnhancedProtectedRoute allowedRoles={[
            'supervisor','finance','hr','it','supply_chain','buyer','project','admin'
          ]}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<AdminAnalyticsDashboard />} />
        </Route>

        <Route path="/sales" element={
          <EnhancedProtectedRoute allowedRoles={['sales','admin','ceo','finance']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<SalesCRM />} />
        </Route>

        <Route path="/payroll" element={
          <EnhancedProtectedRoute allowedRoles={['employee','sales','finance','admin','buyer','hr','supply_chain','technical','hse','it','project','ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<Payroll />} />
        </Route>

        <Route path="/expenses" element={
          <EnhancedProtectedRoute allowedRoles={['employee','sales','finance','admin','buyer','hr','supply_chain','technical','hse','it','project','ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<Expenses />} />
        </Route>

        <Route path="/recruitment" element={
          <EnhancedProtectedRoute allowedRoles={['employee','sales','finance','admin','buyer','hr','supply_chain','technical','hse','it','project','ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<Recruitment />} />
        </Route>

        <Route path="/documents" element={
          <EnhancedProtectedRoute allowedRoles={['employee','sales','finance','admin','buyer','hr','supply_chain','technical','hse','it','project','ceo']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<DocumentHub />} />
        </Route>

        <Route path="/audit-trail" element={
          <EnhancedProtectedRoute allowedRoles={['it','admin','ceo','finance','hr','supply_chain']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<AuditTrail />} />
        </Route>

        <Route path="/data-export" element={
          <EnhancedProtectedRoute allowedRoles={['it','admin','ceo','finance','hr','supply_chain']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<DataExport />} />
        </Route>

        <Route path="/employee-welfare" element={
          <EnhancedProtectedRoute allowedRoles={['hr','admin']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<HREmployeeWelfare />} />
        </Route>

        <Route path="/my-communications" element={
          <ProtectedRoute><PettyCashLayout /></ProtectedRoute>
        }>
          <Route index element={<MyCommunications />} />
        </Route>

        <Route path="/communications/:id/view" element={
          <ProtectedRoute><PettyCashLayout /></ProtectedRoute>
        }>
          <Route index element={<CommunicationDetail readonly={true} />} />
        </Route>

        <Route path="/system-settings" element={
          <EnhancedProtectedRoute allowedRoles={['admin']}>
            <PettyCashLayout />
          </EnhancedProtectedRoute>
        }>
          <Route index element={<AdminSystemSettings />} />
        </Route>

        <Route
          path="/delegation"
          element={<ProtectedRoute><PettyCashLayout /></ProtectedRoute>}
        >
          <Route index    element={<Navigate to="/delegation/settings" replace />} />
          <Route
            path="settings"
            element={<UserDelegationSettings />}
          />
        </Route>

        {/* ── FALLBACK ────────────────────────────────────────────────────── */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />

      </Routes>
    </Router>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// App (root)
// ─────────────────────────────────────────────────────────────────────────────
const App = () => (
  <Provider store={store}>
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1890ff',
          colorPrimaryHover: '#40a9ff',
          colorPrimaryActive: '#096dd9',
          borderRadius: 4,
          colorBgContainer: '#ffffff',
          colorText: '#000000d9',
          colorTextBase: '#000000d9',
        },
        components: {
          Button: {
            colorPrimary: '#1890ff',
            colorPrimaryHover: '#40a9ff',
            colorPrimaryActive: '#096dd9',
            primaryColor: '#ffffff',
            colorPrimaryText: '#ffffff',
            colorTextDisabled: '#00000040',
          },
        },
      }}
    >
      <AntApp>
        <CEODelegationProvider>
          <UserDelegationProvider>
            <AppRoutes />
          </UserDelegationProvider>
        </CEODelegationProvider>
      </AntApp>
    </ConfigProvider>
  </Provider>
);

export default App;


